import { expose } from 'comlink';
import { collection, DocumentSnapshot, getDocs, getFirestore, limit, orderBy, query, Query, QueryConstraint, startAfter } from 'firebase/firestore';
import { Column } from '../../asset/asset.dto';
import { Warehouse } from '../../warehouse/warehouse.dto';
import { WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback } from '../executors';
import { AbstractWarehouseDataSyncStepWorker } from './abstract-warehouse-worker.utils';
import { merge } from 'lodash-es';
import { UPRIGHTS_COLUMNS_COLLECTION, UPRIGHTS_COLUMNS_STORE, WAREHOUSES_STORE, WW_WAREHOUSES_COLLECTION, logger } from '@damofire/data-commons';

const DOWNLOAD_LIMIT = 1000;

export class WarehouseUprightsColumnsStepsWorker extends AbstractWarehouseDataSyncStepWorker<Column> {
  constructor() {
    super(UPRIGHTS_COLUMNS_STORE, 'UPRIGHTS-COLUMNS');
  }

  protected async clean(ctx: WarehouseSyncStepTaskContext, onProgress?: (progress: number) => void): Promise<void> {
    logger.debug('WarehouseUprightsColumnsStepsWorker::clean', { division: ctx.division, category: 'sync' });
    return this.deleteAllDataObsoleteFromStore<Column>(this.store, ctx, (dto) => dto.division._id, undefined, onProgress);
  }

  protected override async resolveRemoteData(ctx: WarehouseSyncStepTaskContext, lastSnapshot: DocumentSnapshot, allFromIndex: Column[]): Promise<[DocumentSnapshot, number]> {
    const queryConstraints: QueryConstraint[] = [limit(DOWNLOAD_LIMIT)];

    if (lastSnapshot) {
      queryConstraints.splice(0, 0, startAfter(lastSnapshot));
    }

    const q: Query = query(collection(getFirestore(), `${WW_WAREHOUSES_COLLECTION}/${ctx.division}/${UPRIGHTS_COLUMNS_COLLECTION}`), orderBy('assetId'), ...queryConstraints);
    const queryResult = await getDocs(q);

    if (queryResult.docs.length && !this.shouldStopTask) {
      const columns = [];
      for (const i of queryResult.docs) {
        let matching: Column & { syncId?: string } = allFromIndex.find((idbDoc) => idbDoc._id === i.id);
        if (matching) {
          merge(matching, { ...i.data(), syncId: ctx.syncId });
        } else {
          matching = { ...i.data(), division: { _id: ctx.division }, _id: i.id, syncId: ctx.syncId };
        }

        columns.push({ ...i.data(), division: { _id: ctx.division }, _id: i.id, syncId: ctx.syncId });
      }

      if (!this.shouldStopTask) {
        logger.info('Downloaded upright columns from Firestore', { count: columns.length, category: 'sync' });
        await this.idbService.updateAll(this.store, columns).catch(console.error);
        return [queryResult.docs.length === DOWNLOAD_LIMIT ? queryResult.docs[queryResult.docs.length - 1] : null, queryResult.docs.length];
      }
    }

    return [null, 0];
  }

  protected async updateTaskProgress(warehouse: string, downloaded: number, onProgress: ProgressCallback): Promise<void> {
    const warehouseData: Warehouse = await this.idbService.getByKey(WAREHOUSES_STORE, warehouse);
    if (warehouseData?.statistics?.assets) {
      // X2 -> Hack en attendant qu'on supporte autre chose que du "selective racking" / Colonne arrière et avant = 2.
      // The assets*2 total is a rough estimate (single-sided uprights have no back
      // column), so it is deliberately NOT reported as `expected`: flagging a
      // shortfall against it would be a false alarm (e.g. 1024/1038 all green).
      const newProgress = Math.ceil((downloaded / (warehouseData.statistics?.assets * 2)) * 100);
      logger.debug('WarehouseUprightsColumnsStepsWorker - Total items downloaded', { progress: newProgress, category: 'sync' });
      onProgress(newProgress, { processed: downloaded });
    }
  }
}

expose(WarehouseUprightsColumnsStepsWorker);
